import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import api from "@/utils/api";
import { CheckCircle2, AlertCircle, AlertTriangle, ChevronRight, Layers, Package, ClipboardCheck } from "lucide-react";

const ProductionStage1 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [operators, setOperators] = useState([]);
  const [machines, setMachines] = useState([]);
  const [tools, setTools] = useState([]);
  const [toolSearch, setToolSearch] = useState("");
  const [batches, setBatches] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [tonnageWarning, setTonnageWarning] = useState("");
  const [checksheetWarning, setChecksheetWarning] = useState("");
  const [opInfo, setOpInfo] = useState(null);  // { is_first_op, op_seq, total_ops, wip_available }

  const [sel, setSel] = useState({ operator: null, machine: null, tool: null, batch: null, subCode: "A", batchNotAvailable: false });

  // First-op tools have a 5-step wizard (RM Batch step), others have 4 steps
  const STEPS = opInfo?.is_first_op === false
    ? ["Operator", "Machine", "Tool", "Confirm"]
    : ["Operator", "Machine", "Tool", "RM Batch", "Confirm"];

  useEffect(() => {
    api.get("/operators").then((r) => setOperators(r.data)).catch(() => {});
    api.get("/machines").then((r) => setMachines(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (toolSearch.length < 1) {
      api.get("/tools").then((r) => setTools(r.data)).catch(() => {});
    } else {
      api.get(`/tools?search=${toolSearch}`).then((r) => setTools(r.data)).catch(() => {});
    }
  }, [toolSearch]);

  // Whenever tool changes, fetch op precheck (first-op? wip available?)
  useEffect(() => {
    if (sel.tool) {
      api.get(`/production/tool-precheck?tool_no=${sel.tool.tool_no}`)
        .then((r) => setOpInfo(r.data))
        .catch(() => setOpInfo(null));
    } else {
      setOpInfo(null);
    }
  }, [sel.tool]);

  useEffect(() => {
    if (sel.tool && sel.machine) {
      const machTon = sel.machine.tonnage || 0;
      const minTon = sel.tool.min_tonnage_t || 0;
      setTonnageWarning(machTon < minTon
        ? `Warning: Machine ${sel.machine.machine_code} (${machTon}T) is below required ${minTon}T for this tool`
        : "");
    }
  }, [sel.tool, sel.machine]);

  useEffect(() => {
    if (sel.machine) {
      api.get(`/machine-check/status?machine_code=${sel.machine.machine_code}`)
        .then((r) => setChecksheetWarning(r.data.submitted ? "" : `Checksheet not submitted for ${sel.machine.machine_code} today`))
        .catch(() => {});
    }
  }, [sel.machine]);

  const loadBatches = () => {
    if (!sel.tool) return;
    const { rm_grade, thickness_mm, width_mm } = sel.tool;
    api.get(`/rm-batches?grade=${rm_grade}&thickness=${thickness_mm}&width=${width_mm}`)
      .then((r) => setBatches(r.data))
      .catch(() => {});
  };

  const next = () => {
    // After Tool step, if first-op load batches; if non-first-op, skip RM Batch step
    if (step === 2 && opInfo?.is_first_op !== false) loadBatches();
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const canNext = () => {
    if (step === 0) return !!sel.operator;
    if (step === 1) return !!sel.machine;
    if (step === 2) {
      if (!sel.tool) return false;
      // Soft check — non-first-op tools without WIP get a warning, but are allowed
      return true;
    }
    if (step === 3 && opInfo?.is_first_op !== false) {
      return sel.batchNotAvailable || !!sel.batch;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (tonnageWarning && !window.confirm("Machine tonnage is below requirement. Continue anyway?")) return;
    setSubmitting(true);
    setError("");
    try {
      const isFirstOp = opInfo?.is_first_op !== false;
      const noBatch = isFirstOp && sel.batchNotAvailable;
      const batchCode = isFirstOp && !noBatch ? (sel.batch?.batch_code || "") : "";
      const payload = {
        operator_name: sel.operator.full_name,
        operator_id: sel.operator.user_id,
        machine_code: sel.machine.machine_code,
        tool_no: sel.tool.tool_no,
        batch_code: batchCode,
        sub_code: sel.subCode,
        manual_batch: noBatch,  // server treats this + empty batch_code as pending_batch_assignment
      };
      const res = await api.post("/production/stage1", payload);
      setSuccess(res.data);
      if (res.data.checksheet_warning) setChecksheetWarning(res.data.checksheet_warning);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header title="Production" showBack backPath="/home" />
        <div className="max-w-md mx-auto px-4 py-12 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={48} className="text-[#15803D]" />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Job Started!</h2>
          <p className="text-slate-500 mb-4">उत्पादन कार्य शुरू हुआ</p>
          <div className="bg-[#1A3560] rounded-2xl p-5 text-white mb-4">
            <p className="text-white/60 text-sm mb-1">Job ID</p>
            <p className="text-3xl font-extrabold tracking-wider">{success.job_id}</p>
            {success.operation_seq && (
              <p className="text-white/70 text-xs mt-2">
                Operation {success.operation_seq} of {success.total_operations}
                {success.is_first_op ? " (First op — RM consumed here)" : " (Pulls from WIP)"}
              </p>
            )}
          </div>
          {success.checksheet_warning && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-700 text-sm flex items-center gap-2">
              <AlertTriangle size={16} /> {success.checksheet_warning}
            </div>
          )}
          {success.wip_warning && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-700 text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> {success.wip_warning}
            </div>
          )}
          {success.fresh_setup && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-blue-800 text-sm flex items-start gap-2 text-left">
              <ClipboardCheck size={18} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-extrabold">Fresh Tool Setup Detected</p>
                <p className="text-xs">
                  {success.replaced_previous_tool
                    ? `Tool T${success.replaced_previous_tool} was auto-unmounted. `
                    : ""}
                  Please fill the Tool Setup Approval Checksheet before running production.
                </p>
                <button
                  data-testid="prod1-open-checksheet-btn"
                  onClick={() => navigate(`/setup-checksheet/new/${success.job_id}`)}
                  className="mt-2 text-xs font-extrabold text-white bg-[#15803D] hover:bg-emerald-700 px-3 py-1.5 rounded-lg"
                >
                  Fill Setup Checksheet
                </button>
              </div>
            </div>
          )}
          {!success.fresh_setup && (
            <div className="mb-4 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-600 text-xs">
              Same tool continues on {success.job_id ? "this machine" : "machine"} — first-part inspection & setup checksheet not required.
            </div>
          )}
          <button
            data-testid="prod1-done-btn"
            onClick={() => navigate("/home")}
            className="w-full h-12 bg-[#1A3560] text-white font-bold rounded-xl active:scale-[0.98] transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <Header title="Start Production Job" showBack backPath="/home" />

      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center flex-1">
                <div className={`flex-1 h-1.5 rounded-full transition-all ${i <= step ? "bg-[#1A3560]" : "bg-slate-200"}`} />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {STEPS.map((s, i) => (
              <span key={s} className={`text-xs font-bold ${i === step ? "text-[#1A3560]" : i < step ? "text-[#15803D]" : "text-slate-300"}`}>
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-4">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
          Step {step + 1} of {STEPS.length} — {STEPS[step]}
        </p>

        {step === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Select Operator / ऑपरेटर चुनें</label>
            <select
              data-testid="operator-select"
              value={sel.operator?.user_id || ""}
              onChange={(e) => {
                const op = operators.find((o) => o.user_id === e.target.value);
                setSel((s) => ({ ...s, operator: op || null }));
              }}
              className="w-full h-12 px-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
            >
              <option value="">-- Select Operator --</option>
              {operators.map((o) => <option key={o.user_id} value={o.user_id}>{o.full_name} ({o.user_id})</option>)}
            </select>
          </div>
        )}

        {step === 1 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Select Machine / मशीन चुनें</label>
            <div className="space-y-2">
              {machines
                .filter(m => m.status === "Active")
                .slice()
                .sort((a, b) =>
                  (a.machine_code || "").localeCompare(b.machine_code || "", undefined, { numeric: true, sensitivity: "base" })
                )
                .map((m) => (
                <button
                  key={m.machine_code}
                  data-testid={`machine-option-${m.machine_code}`}
                  onClick={() => setSel((s) => ({ ...s, machine: m }))}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                    sel.machine?.machine_code === m.machine_code ? "border-[#1A3560] bg-blue-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <span className="font-bold text-sm text-slate-900">{m.machine_code}</span>
                  <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-lg">{m.tonnage}T</span>
                </button>
              ))}
            </div>
            {checksheetWarning && (
              <div className="mt-3 flex items-center gap-2 text-amber-600 text-sm bg-amber-50 rounded-xl px-3 py-2">
                <AlertTriangle size={16} /> {checksheetWarning}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <input
                data-testid="tool-search"
                type="text"
                placeholder="Search tool no, part name..."
                value={toolSearch}
                onChange={(e) => setToolSearch(e.target.value)}
                className="w-full h-12 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
              />
              <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                {tools.map((t) => (
                  <button
                    key={t.tool_no}
                    data-testid={`tool-option-${t.tool_no}`}
                    onClick={() => setSel((s) => ({ ...s, tool: t, batch: null }))}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      sel.tool?.tool_no === t.tool_no ? "border-[#1A3560] bg-blue-50" : "border-slate-100 hover:border-slate-200"
                    }`}
                  >
                    <p className="font-bold text-sm text-slate-900">T{t.tool_no} — {t.part_name}</p>
                    <p className="text-xs text-slate-500">{t.operation} | Part: {t.part_no} | Op {t.operation_seq || "?"}/{t.total_operations || "?"}</p>
                  </button>
                ))}
              </div>
            </div>
            {sel.tool && opInfo && (
              <div className={`rounded-2xl border-2 p-4 space-y-2 ${opInfo.is_first_op ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
                <div className="flex items-center gap-2">
                  <Layers size={18} className={opInfo.is_first_op ? "text-amber-700" : "text-emerald-700"} />
                  <p className={`font-extrabold text-sm ${opInfo.is_first_op ? "text-amber-900" : "text-emerald-900"}`}>
                    Operation {opInfo.op_seq} of {opInfo.total_ops}
                    {opInfo.is_first_op ? " — FIRST OP (RM consumed here)" : " — Pulls from WIP"}
                  </p>
                </div>
                {opInfo.is_first_op ? (
                  <p className="text-xs text-amber-800">
                    Gross weight: <b>{opInfo.gross_weight_g || 0}g</b> per part.
                    {!opInfo.gross_weight_g && <span className="text-red-600"> ⚠ Not configured — RM won't auto-deduct.</span>}
                  </p>
                ) : (
                  <div>
                    <p className="text-xs text-emerald-800">
                      Available WIP at op {opInfo.op_seq - 1}: <b className="text-base">{opInfo.wip_available || 0} pcs</b>
                    </p>
                    {(opInfo.wip_available || 0) === 0 && (
                      <div className="mt-2 flex items-start gap-2 text-amber-800 text-xs bg-amber-100 rounded-xl px-3 py-2">
                        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                        <span>
                          No WIP recorded at the previous op. You can still start the job — the previous op's
                          Stage-2 may be pending. The shortfall will be flagged in the shift-end report for reconciliation.
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {sel.tool && (
              <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Tool Details</p>
                <InfoRow label="Part Name" value={sel.tool.part_name} />
                <InfoRow label="Part No" value={sel.tool.part_no} />
                <InfoRow label="Cavities" value={sel.tool.no_of_cavities} />
                <InfoRow label="Target/Day" value={`${sel.tool.target_per_day} pcs`} />
                <div className="bg-slate-50 rounded-xl p-3 mt-2">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">Required RM</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-xs text-slate-500">Grade</p><p className="font-bold text-sm text-[#1A3560]">{sel.tool.rm_grade}</p></div>
                    <div><p className="text-xs text-slate-500">Thick</p><p className="font-bold text-sm text-[#1A3560]">{sel.tool.thickness_mm}mm</p></div>
                    <div><p className="text-xs text-slate-500">Width</p><p className="font-bold text-sm text-[#1A3560]">{sel.tool.width_mm}mm</p></div>
                  </div>
                </div>
                {tonnageWarning && (
                  <div className="flex items-start gap-2 text-amber-700 text-xs bg-amber-50 rounded-xl px-3 py-2">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {tonnageWarning}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* RM Batch step — only shown for first-op tools (step index 3 in 5-step flow) */}
        {step === 3 && opInfo?.is_first_op !== false && (
          <div className="space-y-3">
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
                {sel.batchNotAvailable
                  ? "Batch Not Available — Admin will map later"
                  : `Matching RM Batches (${batches.length} found)`}
              </p>

              {sel.batchNotAvailable ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-900">
                      <p className="font-bold">Production will start without a linked RM batch.</p>
                      <p className="mt-0.5">An admin will assign the real batch later from Admin → Job Trace → RM Batch Mapping. RM stock and lineage will be updated at that point.</p>
                    </div>
                  </div>
                  <button
                    data-testid="batch-not-avail-undo"
                    onClick={() => setSel((s) => ({ ...s, batchNotAvailable: false }))}
                    className="text-xs font-bold bg-white border border-amber-300 text-amber-800 px-3 py-1.5 rounded-lg hover:bg-amber-100"
                  >
                    ← Back to batch list
                  </button>
                </div>
              ) : batches.length === 0 ? (
                <div className="text-center py-6 text-slate-400">
                  <Package size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No matching RM batches found</p>
                  <p className="text-xs">for {sel.tool?.rm_grade} / {sel.tool?.thickness_mm}mm / {sel.tool?.width_mm}mm</p>
                  <button
                    data-testid="batch-not-avail-empty"
                    onClick={() => setSel((s) => ({ ...s, batchNotAvailable: true, batch: null }))}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 px-3 py-2 rounded-lg transition-all"
                  >
                    <AlertTriangle size={12} /> Batch not available — admin maps later
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {batches.map((b) => (
                    <button
                      key={b.batch_code}
                      data-testid={`batch-option-${b.batch_code}`}
                      onClick={() => setSel((s) => ({ ...s, batch: b, batchNotAvailable: false }))}
                      className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                        sel.batch?.batch_code === b.batch_code ? "border-[#15803D] bg-green-50" : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <p className="font-bold text-sm text-slate-900">{b.batch_code}</p>
                      <p className="text-xs text-slate-500">{b.supplier_name} • {b.date}</p>
                      <div className="flex gap-3 mt-1">
                        <span className="text-xs text-slate-500">Total: <b>{b.quantity_kg}kg</b></span>
                        <span className={`text-xs font-medium ${b.remaining_qty < b.quantity_kg * 0.2 ? "text-red-600" : "text-green-600"}`}>
                          Remaining: {b.remaining_qty}kg
                        </span>
                      </div>
                    </button>
                  ))}
                  <button
                    data-testid="batch-not-avail-list"
                    onClick={() => setSel((s) => ({ ...s, batchNotAvailable: true, batch: null }))}
                    className="w-full mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-bold bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 px-3 py-2.5 rounded-lg transition-all"
                  >
                    <AlertTriangle size={12} /> None of these — batch not available (admin will map later)
                  </button>
                </div>
              )}
            </div>
            {(sel.batch || sel.batchNotAvailable) && !sel.batchNotAvailable && (
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Sub-Code</label>
                <div className="flex gap-2">
                  {["A", "B", "C", "D"].map((code) => (
                    <button
                      key={code}
                      data-testid={`subcode-${code}`}
                      onClick={() => setSel((s) => ({ ...s, subCode: code }))}
                      className={`h-12 px-6 rounded-xl font-bold text-base transition-all ${
                        sel.subCode === code ? "bg-[#1A3560] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Confirm step — last step, index varies */}
        {isLastStep && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Confirm 4M / पुष्टि करें</p>
            <div className="bg-blue-50 rounded-xl p-4 space-y-2">
              <SummaryRow icon="👤" label="Man (Operator)" value={sel.operator?.full_name} />
              <SummaryRow icon="⚙️" label="Machine" value={`${sel.machine?.machine_code} (${sel.machine?.tonnage}T)`} />
              {opInfo?.is_first_op === false ? (
                <SummaryRow icon="📥" label="WIP Source" value={`Op ${opInfo.op_seq - 1} • ${opInfo.wip_available} pcs available`} />
              ) : sel.batchNotAvailable ? (
                <SummaryRow icon="⚠️" label="Batch" value="Not available — admin will map later" />
              ) : (
                <SummaryRow icon="📦" label="Material (Batch)" value={`${sel.batch?.batch_code}-${sel.subCode}`} />
              )}
              <SummaryRow icon="🔧" label="Method (Tool)" value={`T${sel.tool?.tool_no} — ${sel.tool?.part_name}`} />
              <SummaryRow icon="🔢" label="Operation" value={`${opInfo?.op_seq || "?"} of ${opInfo?.total_ops || "?"}${opInfo?.is_last_op ? " (LAST → produces FG)" : ""}`} />
              <SummaryRow icon="🏭" label="Part No" value={sel.tool?.part_no} />
              <SummaryRow icon="🎯" label="Target/Day" value={`${sel.tool?.target_per_day} pcs`} />
            </div>
            {tonnageWarning && (
              <div className="flex items-start gap-2 text-amber-700 text-sm bg-amber-50 rounded-xl px-3 py-2">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> {tonnageWarning}
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 rounded-xl px-3 py-2">
                <AlertCircle size={16} /> {error}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-md mx-auto flex gap-3">
          {step > 0 && (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="h-14 px-6 rounded-xl border-2 border-slate-300 text-slate-700 font-bold active:scale-[0.97] transition-all"
            >
              Back
            </button>
          )}
          {!isLastStep ? (
            <button
              data-testid="prod1-next-btn"
              onClick={next}
              disabled={!canNext()}
              className="flex-1 h-14 bg-[#1A3560] disabled:bg-slate-200 text-white disabled:text-slate-400 font-bold rounded-xl active:scale-[0.97] transition-all flex items-center justify-center gap-2"
            >
              Next — {STEPS[step + 1]} <ChevronRight size={18} />
            </button>
          ) : (
            <button
              data-testid="prod1-submit-btn"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 h-14 bg-[#15803D] disabled:bg-slate-300 text-white font-bold rounded-xl active:scale-[0.97] transition-all flex items-center justify-center gap-2"
            >
              {submitting ? <><div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Starting...</> : "Start Job / कार्य शुरू करें"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between items-center py-1">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="font-bold text-sm text-slate-900">{value}</span>
  </div>
);

const SummaryRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    <span className="text-lg">{icon}</span>
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="font-bold text-sm text-slate-900">{value}</p>
    </div>
  </div>
);

export default ProductionStage1;
